/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: EmailTemplatesListView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the email-templates module.
----------------------------------------------------------------*/

import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import DataGridSimple from '@/components/ui/DataGridSimple';
import { useNavigationStore } from '@/store/navigation';
import { useEmailTemplateList } from './hooks/useEmailTemplateList';
import { EmailTemplatesActions } from './components/EmailTemplatesActions';
import { ModulePageContainer } from '@/components/layouts';
import { EmailTemplate } from './api/emailTemplatesApi';

export default function EmailTemplatesListView() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const { setCurrentPageTitle } = useNavigationStore();

    useEffect(() => {
        setCurrentPageTitle(t('emailTemplates.title'));
        return () => setCurrentPageTitle(null);
    }, [setCurrentPageTitle, t]);

    const {
        data: templates,
        loading,
        columns,
        pagination,
        searchQuery,
        sortBy,
        sortOrder,
        handleSearch,
        handleSort,
        handlePageChange,
        handleItemsPerPageChange,
        handleColumnToggle,
        refresh
    } = useEmailTemplateList();

    const handleEdit = useCallback((template: EmailTemplate) => {
        navigate(`/admin/system-settings/email-template/${template.id}`);
    }, [navigate]);

    const renderActions = useCallback(
        (template: EmailTemplate) => (
            <EmailTemplatesActions
                template={template}
                onEdit={handleEdit}
            />
        ),
        [handleEdit]
    );

    return (
        <ModulePageContainer
            currentView="list"
            listView={
                <DataGridSimple
                    titleIcon={<Mail className="h-5 w-5 text-primary" />}
                    title={t('emailTemplates.title')}
                    data={templates}
                    loading={loading}
                    columns={columns}
                    onColumnToggle={handleColumnToggle}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    searchQuery={searchQuery}
                    onSearch={handleSearch}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    searchPlaceholder={t('emailTemplates.actions.search')}
                    emptyMessage={t('emailTemplates.messages.noData')}
                    renderActions={renderActions}
                    translations={{
                        items: t('common.items') || 'Items',
                        columns: t('common.columns') || 'Columns',
                        noItemsFound: t('common.noItemsFound') || 'No items found',
                        showing: t('common.showing') || 'Showing',
                        to: t('common.to') || 'to',
                        of: t('common.of') || 'of',
                        results: t('common.results') || 'results',
                        previous: t('common.previous') || 'Previous',
                        next: t('common.next') || 'Next',
                        page: t('common.page') || 'Page',
                        deleteItem: t('common.deleteItem') || 'Delete Item',
                        areYouSureDeleteItem: t('common.areYouSureDeleteItem') || 'Are you sure?',
                        delete: t('common.delete') || 'Delete',
                        cancel: t('common.cancel') || 'Cancel',
                        allSelected: t('common.allSelected') || 'All selected',
                        selected: t('common.selected') || 'selected',
                        searchOptions: t('common.searchOptions') || 'Search options',
                        noOptionsFound: t('common.noOptionsFound') || 'No options found',
                        selectAll: t('common.selectAll') || 'Select all',
                        clearAll: t('common.clearAll') || 'Clear all'
                    }}
                />
            }
            formView={null}
        />
    );
}
